/**
 * Cleanup Utilities
 * 
 * This module provides utilities for managing cleanup operations
 * and preventing memory leaks in the application.
 */

export class CleanupManager {
  private cleanupFunctions: (() => void)[] = [];
  private isCleanedUp = false;

  /**
   * Register a cleanup function to be called on cleanup
   */
  register(cleanupFn: () => void): void {
    if (this.isCleanedUp) {
      // If already cleaned up, execute immediately
      cleanupFn();
      return;
    }
    this.cleanupFunctions.push(cleanupFn);
  }

  /**
   * Execute all registered cleanup functions
   */
  cleanup(): void {
    if (this.isCleanedUp) return;
    
    this.isCleanedUp = true;
    this.cleanupFunctions.forEach(fn => {
      try {
        fn();
      } catch (error) {
        console.error('Error during cleanup:', error);
      }
    });
    this.cleanupFunctions = [];
  }

  /**
   * Check if cleanup has been performed
   */
  get hasCleanedUp(): boolean {
    return this.isCleanedUp;
  }
}

/**
 * Global timeout manager to prevent timeout accumulation
 */
export class TimeoutManager {
  private timeouts = new Map<string, NodeJS.Timeout>();

  /**
   * Set a timeout with a unique key
   */
  setTimeout(key: string, callback: () => void, delay: number): void {
    // Clear existing timeout if it exists
    this.clearTimeout(key);
    
    const timeoutId = setTimeout(() => {
      this.timeouts.delete(key);
      callback();
    }, delay);
    
    this.timeouts.set(key, timeoutId);
  }

  /**
   * Clear a specific timeout
   */
  clearTimeout(key: string): void {
    const timeoutId = this.timeouts.get(key);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.timeouts.delete(key);
    }
  }

  /**
   * Clear all timeouts
   */
  clearAll(): void {
    this.timeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.timeouts.clear();
  }
}

/**
 * DOM element cleanup utility
 */
export function cleanupDOMElement(element: HTMLElement | null): void {
  if (!element || !element.parentNode) return;
  
  try {
    // Remove all event listeners by cloning and replacing
    const clone = element.cloneNode(true) as HTMLElement;
    element.parentNode.replaceChild(clone, element);
    clone.remove();
  } catch (_error) {
    // Fallback to simple removal
    try {
      element.remove();
    } catch (removeError) {
      console.error('Failed to remove DOM element:', removeError);
    }
  }
}

/**
 * Create a cleanup-aware event listener
 */
export function addCleanupAwareEventListener<K extends keyof WindowEventMap>(
  target: Window | Document | HTMLElement,
  type: K,
  listener: (this: Window, ev: WindowEventMap[K]) => any,
  options?: boolean | AddEventListenerOptions
): () => void {
  target.addEventListener(type as string, listener as EventListener, options);
  
  return () => {
    target.removeEventListener(type as string, listener as EventListener, options);
  };
} 
